import React, { useState, useEffect } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  TextField,
  Typography,
  MenuItem,
  Button,
} from '@mui/material';
import CustomSelect from '../CustomSelect';
import { LocalizationProvider, MobileDateTimePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import CreateEditTriggerStyles from '../../../../components/Triggers/CreateEditTrigger/styles/CreateEditTriggerStyles';
import SaveCancelButtons from '../SaveCancelComponent/SaveCancelButtons';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';

interface CustomRecurrenceDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: { recurrence: any; startDate?: Date | null; endDate?: Date | null; totalOccurrence: number; durationNumber?: number; durationUnit?: string }) => void;
  initialData?: {
    recurrence?: any;
    startDate?: Date | null;
    endDate?: Date | null;
    totalOccurrence?: number;
    durationNumber?: number;
    durationUnit?: string;
  };
  isStartDateActive?: boolean;
  isDurationActive?: boolean;
  isEndDateActive?: boolean;
  isCustomRecurrenceAvailable?: boolean;
  isEndDateOptional?: boolean;
}

// 
export const RECURRENCE_FREQUENCY_TYPES = {
  HOURLY: 'HOURLY',
  DAILY: 'DAILY',
  WEEKLY: 'WEEKLY',
  MONTHLY: 'MONTHLY',
  YEARLY: 'YEARLY',
  CUSTOM: 'CUSTOM',
};

const dayMapping: { [key: string]: number } = {
  Monday: 0,
  Tuesday: 1,
  Wednesday: 2,
  Thursday: 3,
  Friday: 4,
  Saturday: 5,
  Sunday: 6,
};

const daysOfWeekOptions = [
  { label: 'M', value: 'Monday' },
  { label: 'T', value: 'Tuesday' },
  { label: 'W', value: 'Wednesday' },
  { label: 'T', value: 'Thursday' },
  { label: 'F', value: 'Friday' },
  { label: 'S', value: 'Saturday' },
  { label: 'S', value: 'Sunday' },
];

const getDurationUnitOptions = (durationNumber: number) => [
  { label: durationNumber > 1 ? 'Minutes' : 'Minute', value: 'minute' },
  { label: durationNumber > 1 ? 'Hours' : 'Hour', value: 'hour' },
  { label: durationNumber > 1 ? 'Days' : 'Day', value: 'day' },
  { label: durationNumber > 1 ? 'Weeks' : 'Week', value: 'week' },
  { label: durationNumber > 1 ? 'Months' : 'Month', value: 'month' },
];

const mapUnitToFrequency = (unit: string): string => {
  switch (unit) {
    case 'Hour':
      return RECURRENCE_FREQUENCY_TYPES.HOURLY;
    case 'Day':
      return RECURRENCE_FREQUENCY_TYPES.DAILY;
    case 'Week':
      return RECURRENCE_FREQUENCY_TYPES.WEEKLY;
    case 'Month':
      return RECURRENCE_FREQUENCY_TYPES.MONTHLY;
    case 'Year':
      return RECURRENCE_FREQUENCY_TYPES.YEARLY;
    default:
      return RECURRENCE_FREQUENCY_TYPES.CUSTOM;
  }
};

const mapFrequencyToUnit = (frequency: string): string => {
  switch (frequency) {
    case RECURRENCE_FREQUENCY_TYPES.HOURLY:
      return 'Hour';
    case RECURRENCE_FREQUENCY_TYPES.DAILY:
      return 'Day';
    case RECURRENCE_FREQUENCY_TYPES.WEEKLY:
      return 'Week';
    case RECURRENCE_FREQUENCY_TYPES.MONTHLY:
      return 'Month';
    case RECURRENCE_FREQUENCY_TYPES.YEARLY:
      return 'Year';
    default:
      return 'Week';
  }
};

const CustomRecurrenceDialog: React.FC<CustomRecurrenceDialogProps> = ({
  open,
  onClose,
  onSubmit,
  initialData,
  isStartDateActive,
  isDurationActive = false,
  isEndDateActive = true,
  isCustomRecurrenceAvailable = true,
  isEndDateOptional= true,
}) => {
  const [interval, setInterval] = useState<number>(1);
  const [unit, setUnit] = useState<string>('Week');
  const [daysOfWeek, setDaysOfWeek] = useState<number[]>([]);
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [totalOccurrence, setTotalOccurrence] = useState<number | null>(null);
  const [durationNumber, setDurationNumber] = useState<number>(1);
  const [durationUnit, setDurationUnit] = useState<string>('hour');

  useEffect(() => {
    if (open && initialData) {
      // Initialize form with existing data
      setInterval(initialData.recurrence?.interval || 1);
      setUnit(mapFrequencyToUnit(initialData.recurrence?.frequency || 'WEEKLY'));
      setDaysOfWeek(initialData.recurrence?.daysOfWeek || []);
      setStartDate(initialData.startDate || null);
      setEndDate(initialData.endDate || null);
      setTotalOccurrence(initialData.totalOccurrence || null);
      setDurationNumber(initialData.durationNumber || 1);
      setDurationUnit(initialData.durationUnit || 'hour');
    } else {
      // Reset form for new entries
      setInterval(1);
      setUnit('Week');
      setDaysOfWeek([]);
      setStartDate(null);
      setEndDate(null);
      setTotalOccurrence(null);
      setDurationNumber(1);
      setDurationUnit('hour');
    }
  }, [open, initialData]);

  const handleDayToggle = (dayName: string) => {
    const dayNum = dayMapping[dayName];
    setDaysOfWeek(prev =>
      prev.includes(dayNum) ? prev.filter(d => d !== dayNum) : [...prev, dayNum]
    );
  };

  const handleSubmitRecurrence = () => {
    if (isStartDateActive && !startDate) {
      toast.error("Please select a start date.");
      return;
    }
    if (isDurationActive && (!durationNumber || !durationUnit) ) {
      toast.error("Please enter a duration number.");
      return;
    }
    if (isEndDateActive && !isEndDateOptional && !endDate) {
      toast.error("Please select an end date.");
      return;
    }

    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      toast.error("Start date cannot be greater than end date");
      return;
    }
  
    const frequency = mapUnitToFrequency(unit);
    const recurrenceData = {
      frequency,
      interval,
      daysOfWeek: frequency === RECURRENCE_FREQUENCY_TYPES.WEEKLY ? daysOfWeek : [],
      dayOfMonth: [],
      weekOfMonth: [],
      month: [],
    };

    onSubmit({
      recurrence: recurrenceData,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      totalOccurrence: totalOccurrence || 0,
      durationNumber: durationNumber,
      durationUnit: durationUnit,
    });
    onClose();
  };

  const getDayButtonState = (dayValue: string) => {
    const dayNumber = dayMapping[dayValue];
    return daysOfWeek.includes(dayNumber);
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ style: { borderRadius: 20, width: '100%' } }}
    >
      <DialogTitle>Custom Recurrence</DialogTitle>
      <DialogContent>

        {isStartDateActive && (
          <>
            <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
              Start Date
            </Typography>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
            <MobileDateTimePicker
              label="Start Date & Time"
              value={startDate ? dayjs(startDate) : null}
              onChange={date => setStartDate(date ? date.toDate() : null)}
              minDateTime={dayjs()}
              format="DD/MM/YYYY HH:mm"
              slotProps={{
                textField: { fullWidth: true, sx: { mb: 2 } },
              }}
            />
            </LocalizationProvider>
          </>
        )}
        {isDurationActive && (
          <>
            <Typography mb={2} p={1} fontSize="12px">
              Duration
            </Typography>
            <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  label="Duration"
                  type="number"
                  value={durationNumber}
                  onKeyDown={(evt) => ["e", "E", "+", "-", "."].includes(evt.key) && evt.preventDefault()}
                  onChange={e => setDurationNumber(Number(e.target.value))}
                  inputProps={{ min: 1 }}
                  variant="outlined"
                />
              </Grid>
              <Grid item xs={6}>
                <CustomSelect
                  label="Unit"
                  options={getDurationUnitOptions(durationNumber)}
                  value={durationUnit}
                  onChange={(option) => setDurationUnit(option.value)}
                />
              </Grid>
            </Grid>
            
          </>
        )}

        <Typography mb={2} p={1} fontSize="12px">
          Interval
        </Typography>

        <Grid container spacing={2} sx={{ marginBottom: '16px' }}>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="No. of times"
              type="number"
              value={interval}
              onChange={e => setInterval(Number(e.target.value))}
              inputProps={{ min: 1 }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Unit (Time)"
              select
              value={unit}
              onChange={e => {
                setUnit(e.target.value);
                setDaysOfWeek([]);
              }}
            >
              <MenuItem value="Hour">{interval > 1 ? 'Hours' : 'Hour'}</MenuItem>
              <MenuItem value="Day">{interval > 1 ? 'Days' : 'Day'}</MenuItem>
              <MenuItem value="Week">{interval > 1 ? 'Weeks' : 'Week'}</MenuItem>
              <MenuItem value="Month">{interval > 1 ? 'Months' : 'Month'}</MenuItem>
              <MenuItem value="Year">{interval > 1 ? 'Years' : 'Year'}</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {unit === 'Week' && (
          <>
            <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
              Days of Week
            </Typography>
            <Box
              display="flex"
              justifyContent="space-around"
              alignItems="center"
              flexWrap="wrap"
              sx={{
                marginBottom: '16px',
                gap: '8px',
                '@media (max-width: 600px)': { justifyContent: 'center' },
              }}
            >
              {daysOfWeekOptions.map((day, index) => (
                <Button
                  size="small"
                  key={index}
                  onClick={() => handleDayToggle(day.value)}
                  sx={{
                    width: '30px',
                    height: '30px',
                    minWidth: '30px',
                    borderRadius: '50%',
                    color: getDayButtonState(day.value) ? 'white' : 'inherit',
                    backgroundColor: getDayButtonState(day.value) ? '#2196F3' : 'lightgrey',
                    padding: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      backgroundColor: getDayButtonState(day.value) ? '#657ED4' : 'lightgrey',
                      transform: 'scale(1.5)',
                    },
                  }}
                >
                  {day.label}
                </Button>
              ))}
            </Box>
          </>
        )}
      {isEndDateActive && (
        <>
        <Typography variant="subtitle2" sx={{ marginBottom: '8px' }}>
          End Date {isEndDateOptional ? '(optional)' : ''} 
        </Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <MobileDateTimePicker
            label="End Date & Time"
            value={endDate ? dayjs(endDate) : null}
            onChange={date => setEndDate(date ? date.toDate() : null)}
            minDateTime={dayjs()}
            format="DD/MM/YYYY HH:mm"
            slotProps={{
              textField: { fullWidth: true, sx: { mb: 2 } },
            }}
          />
        </LocalizationProvider>
        </>
        )}
        {isCustomRecurrenceAvailable &&
        (
        <TextField
          fullWidth
          label="Total Occurrences"
          placeholder="Enter the Number of Occurrences"
          type="number"
          value={totalOccurrence ?? ''}
          onChange={e => setTotalOccurrence(Number(e.target.value))}
          inputProps={{ min: 1 }}
          sx={{ marginBottom: '16px', marginTop: '16px' }}
        />
        )
        }
      </DialogContent>
      <Box sx={CreateEditTriggerStyles.saveCancelButtons}>
        <SaveCancelButtons
          saveValue={initialData ? 'UPDATE' : 'SUBMIT'}
          containerStyle={CreateEditTriggerStyles.saveCancelContainer}
          cancelButtonStyle={CreateEditTriggerStyles.cancelButtonDialog}
          saveButtonStyle={CreateEditTriggerStyles.saveButtonDialog}
          onSave={handleSubmitRecurrence}
          onCancel={onClose}
        />
      </Box>
    </Dialog>
  );
};

export default CustomRecurrenceDialog;
